import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import Layout from '../components/Layout';
import axios from 'axios';
import moment from 'moment-timezone';
import './Upload.css';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';
const APP_TIMEZONE = process.env.REACT_APP_TIMEZONE || 'America/New_York';

const Upload = () => {
  const navigate = useNavigate();
  const [dragActive, setDragActive] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [timesheets, setTimesheets] = useState([]);
  const [loading, setLoading] = useState(true);
  
  // Determine which week to upload for based on current day
  // If today is before Thursday (Sun, Mon, Tue, Wed): use last week
  // If today is Thursday or after (Thu, Fri, Sat): use this week
  const getUploadWeek = () => {
    const today = moment.tz(APP_TIMEZONE);
    const dayOfWeek = today.day(); // 0=Sunday, 3=Wednesday, 4=Thursday
    
    if (dayOfWeek < 4) {
      // Before Thursday - upload for last week
      return {
        start: moment.tz(APP_TIMEZONE).subtract(1, 'week').startOf('week'),
        end: moment.tz(APP_TIMEZONE).subtract(1, 'week').endOf('week')
      };
    } else {
      // Thursday or after - upload for this week
      return {
        start: moment.tz(APP_TIMEZONE).startOf('week'),
        end: moment.tz(APP_TIMEZONE).endOf('week')
      };
    }
  };
  
  const [currentWeek] = useState(getUploadWeek());

  // Helper function to check if current week is eligible for upload
  // Anyone can upload screenshot for the week until next Wednesday
  const isUploadAllowed = () => {
    const today = moment.tz(APP_TIMEZONE).startOf('day');
    const weekEndDate = currentWeek.end;
    
    // Find the Wednesday after the week ends
    const nextWednesday = weekEndDate.clone().day(10); // day(10) = next week Wednesday
    
    // Can upload until (and including) the next Wednesday
    return today.isSameOrBefore(nextWednesday, 'day');
  };

  const fetchTimesheets = useCallback(async () => {
    try {
      const response = await axios.get(`${API_URL}/upload/my-timesheets`);
      setTimesheets(response.data.timesheets);
    } catch (error) {
      console.error('Error fetching timesheets:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchTimesheets();
  }, [fetchTimesheets]);

  // Set up Server-Sent Events for real-time updates
  useEffect(() => {
    const token = localStorage.getItem('token');
    if (!token) return;

    // EventSource doesn't support custom headers, so we pass token as query parameter
    const eventSourceUrl = `${API_URL}/events/timesheet-events?token=${encodeURIComponent(token)}`;
    const eventSource = new EventSource(eventSourceUrl);

    eventSource.onmessage = (event) => {
      try {
        const data = JSON.parse(event.data);
        
        if (data.type === 'timesheet-processed') {
          // Show success notification
          alert('✓ Timesheet processed successfully!');
          // Refresh the timesheets list when a timesheet is processed
          fetchTimesheets();
        } else if (data.type === 'timesheet-error') {
          // Show error notification with the error message
          const errorMsg = data.error || 'An error occurred while processing your timesheet';
          alert(`⚠️ Timesheet Processing Error\n\n${errorMsg}`);
          // Refresh the timesheets list to show error status
          fetchTimesheets();
        }
      } catch (error) {
        console.error('Error parsing SSE event:', error);
      }
    };

    eventSource.onerror = (error) => {
      console.error('SSE connection error:', error);
      // EventSource will automatically reconnect
    };

    return () => {
      eventSource.close();
    };
  }, [fetchTimesheets]);

  const handleDrag = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === 'dragenter' || e.type === 'dragover') {
      setDragActive(true);
    } else if (e.type === 'dragleave') {
      setDragActive(false);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);

    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      handleFile(e.dataTransfer.files[0]);
    }
  };

  const handleFileInput = (e) => {
    if (e.target.files && e.target.files[0]) {
      handleFile(e.target.files[0]);
    }
  };

  const handleFile = async (file) => {
    // Allow PDF and image files
    const allowedTypes = [
      'application/pdf',
      'image/jpeg',
      'image/jpg',
      'image/png',
      'image/gif',
      'image/webp'
    ];
    
    if (!allowedTypes.includes(file.type)) {
      alert('Only PDF and image files (JPG, PNG, GIF, WEBP) are allowed');
      return;
    }

    try {
      setUploading(true);
      
      // Convert file to base64
      const base64Data = await new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => {
          // Remove the data URL prefix (e.g., "data:image/png;base64,")
          const base64 = reader.result.split(',')[1];
          resolve(base64);
        };
        reader.onerror = reject;
        reader.readAsDataURL(file);
      });

      // Send JSON data instead of FormData
      await axios.post(`${API_URL}/upload`, {
        base64Data: base64Data,
        mimeType: file.type,
        fileName: file.name,
        weekStartDate: currentWeek.start.format('YYYY-MM-DD'),
        weekEndDate: currentWeek.end.format('YYYY-MM-DD')
      }, {
        headers: {
          'Content-Type': 'application/json'
        }
      });
      
      alert('File uploaded successfully!');
      fetchTimesheets();
    } catch (error) {
      alert(error.response?.data?.message || 'Upload failed');
    } finally {
      setUploading(false);
    }
  };

  const handleSubmit = async (id) => {
    try {
      await axios.patch(`${API_URL}/upload/${id}/submit`);
      // Set flag in sessionStorage for dashboard to show notification
      sessionStorage.setItem('showSubmitNotification', 'true');
      // Navigate to dashboard
      navigate('/dashboard');
    } catch (error) {
      alert(error.response?.data?.message || 'Submission failed');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this timesheet?')) {
      return;
    }

    try {
      await axios.delete(`${API_URL}/upload/${id}`);
      alert('Timesheet deleted successfully!');
      fetchTimesheets();
    } catch (error) {
      alert(error.response?.data?.message || 'Deletion failed');
    }
  };

  const getStatusBadge = (status) => {
    const statusClasses = {
      pending: 'status-pending',
      processing: 'status-processing',
      completed: 'status-completed',
      submitted: 'status-submitted',
      error: 'status-error'
    };

    return (
      <span className={`status-badge ${statusClasses[status] || ''}`}>
        {status.charAt(0).toUpperCase() + status.slice(1)}
      </span>
    );
  };

  const uploadAllowed = isUploadAllowed();

  // Find processing, completed, and error timesheets for the current week
  const processingTimesheet = timesheets.find(ts => 
    ts.status === 'processing' && 
    moment(ts.weekStartDate).isSame(currentWeek.start, 'day')
  );
  
  const completedTimesheet = timesheets.find(ts => 
    ts.status === 'completed' && 
    moment(ts.weekStartDate).isSame(currentWeek.start, 'day')
  );

  const errorTimesheet = timesheets.find(ts => 
    ts.status === 'error' && 
    moment(ts.weekStartDate).isSame(currentWeek.start, 'day')
  );

  return (
    <Layout>
      <div className="upload-page">
        <h1>Upload Weekly Time-Sheet</h1>

        {processingTimesheet && (
          <div className="alert-info" style={{ 
            backgroundColor: '#d1ecf1', 
            border: '1px solid #17a2b8',
            borderRadius: '4px',
            padding: '12px 16px',
            marginBottom: '20px',
            color: '#0c5460',
            display: 'flex',
            alignItems: 'center',
            gap: '8px'
          }}>
            <div className="spinner" style={{
              border: '2px solid #17a2b8',
              borderTop: '2px solid transparent',
              borderRadius: '50%',
              width: '16px',
              height: '16px',
              animation: 'spin 1s linear infinite'
            }}></div>
            <strong>📸 Screenshot is in processing...</strong>
          </div>
        )}

        {errorTimesheet && !processingTimesheet && (
          <div className="alert-error" style={{ 
            backgroundColor: '#f8d7da', 
            border: '1px solid #dc3545',
            borderRadius: '4px',
            padding: '12px 16px',
            marginBottom: '20px',
            color: '#721c24',
            display: 'flex',
            alignItems: 'flex-start',
            justifyContent: 'space-between',
            gap: '12px'
          }}>
            <div style={{ flex: 1 }}>
              <strong>⚠️ Screenshot Processing Error</strong>
              <p style={{ margin: '4px 0 0 0', fontSize: '14px' }}>
                {errorTimesheet.errorMessage || 'An error occurred while processing your timesheet'}
              </p>
            </div>
            <button
              className="btn-small"
              onClick={() => handleDelete(errorTimesheet.id)}
              style={{
                padding: '6px 12px',
                borderRadius: '4px',
                border: '1px solid #dc3545',
                backgroundColor: 'white',
                color: '#dc3545',
                cursor: 'pointer',
                fontWeight: 'bold',
                fontSize: '13px',
                whiteSpace: 'nowrap'
              }}
            >
              Delete & Re-upload
            </button>
          </div>
        )}

        {completedTimesheet && !processingTimesheet && !errorTimesheet && (
          <div className="alert-success" style={{ 
            backgroundColor: '#d4edda', 
            border: '1px solid #28a745',
            borderRadius: '4px',
            padding: '12px 16px',
            marginBottom: '20px',
            color: '#155724',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between'
          }}>
            <div>
              <strong>✓ Screenshot processing is finished!</strong>
              <p style={{ margin: '4px 0 0 0', fontSize: '14px' }}>
                Your timesheet is ready to submit.
              </p>
            </div>
            <button
              className="btn-primary"
              onClick={() => handleSubmit(completedTimesheet.id)}
              style={{
                padding: '8px 16px',
                borderRadius: '4px',
                border: 'none',
                backgroundColor: '#28a745',
                color: 'white',
                cursor: 'pointer',
                fontWeight: 'bold',
                fontSize: '14px'
              }}
            >
              Submit Now
            </button>
          </div>
        )}

        {!uploadAllowed && (
          <div className="alert-warning" style={{ 
            backgroundColor: '#fff3cd', 
            border: '1px solid #ffc107',
            borderRadius: '4px',
            padding: '12px 16px',
            marginBottom: '20px',
            color: '#856404'
          }}>
            <strong>⚠️ Upload Deadline Passed</strong>
            <p style={{ margin: '4px 0 0 0' }}>
              Screenshots can only be uploaded until the Wednesday following the week end.
              The deadline for the current week (ending {currentWeek.end.format('MMM DD, YYYY')}) has passed.
            </p>
          </div>
        )}

        <div
          className={`upload-area ${dragActive ? 'drag-active' : ''} ${!uploadAllowed ? 'upload-disabled' : ''}`}
          onDragEnter={uploadAllowed ? handleDrag : undefined}
          onDragLeave={uploadAllowed ? handleDrag : undefined}
          onDragOver={uploadAllowed ? handleDrag : undefined}
          onDrop={uploadAllowed ? handleDrop : undefined}
          style={!uploadAllowed ? { opacity: 0.6, cursor: 'not-allowed' } : {}}
        >
          <div className="upload-icon">☁️</div>
          <p>Drag and drop your weekly screenshot (PDF or image) here, or click to browse.</p>
          <input
            type="file"
            id="file-input"
            accept=".pdf,.jpg,.jpeg,.png,.gif,.webp"
            onChange={handleFileInput}
            style={{ display: 'none' }}
            disabled={!uploadAllowed}
          />
          <button
            className="browse-button"
            onClick={() => uploadAllowed && document.getElementById('file-input').click()}
            disabled={uploading || !uploadAllowed}
          >
            Browse Files
          </button>
          {uploading && <p className="uploading-text">Uploading...</p>}
        </div>

        <p className="upload-note">
          *PDF and image files (JPG, PNG, GIF, WEBP) are accepted. Please ensure your file contains clear screenshots of your time-sheet application.
        </p>

        <div className="guidelines-section">
          <h2>File Upload Guidelines</h2>
          <ul>
            <li>Ensure your screenshot clearly shows the full weekly time-sheet with daily entries and totals.</li>
            <li>Save your screenshot as a PDF or image file (e.g., "FirstName_LastName_WeekEndingDate.pdf" or ".png").</li>
            <li>Supported formats: PDF, JPG, PNG, GIF, WEBP</li>
            <li>Verify the file is legible and all text is clear for accurate parsing.</li>
            <li>Avoid cropping or blurring important sections of the time-sheet.</li>
            <li>For any issues, please contact support with the error details.</li>
          </ul>
        </div>

        <div className="recent-uploads-section">
          <div className="section-header">
            <h2>My Recent Uploads</h2>
            {/* <button className="btn-primary">Submit All Pending</button> */}
          </div>

          {loading ? (
            <div className="loading">Loading...</div>
          ) : (
            <table className="uploads-table">
              <thead>
                <tr>
                  <th>File Name</th>
                  <th>Upload Date</th>
                  <th>Status</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {timesheets.length === 0 ? (
                  <tr>
                    <td colSpan="4" className="no-data">No uploads yet</td>
                  </tr>
                ) : (
                  timesheets.map((ts) => (
                    <React.Fragment key={ts.id}>
                      <tr>
                        <td>{ts.fileName}</td>
                        <td>{moment(ts.uploadedAt).format('YYYY-MM-DD HH:mm A')}</td>
                        <td>{getStatusBadge(ts.status)}</td>
                        <td>
                          <div className="action-buttons">
                            {ts.status === 'completed' && (
                              <button
                                className="btn-small btn-primary"
                                onClick={() => handleSubmit(ts.id)}
                              >
                                Submit
                              </button>
                            )}
                            <button className="btn-small btn-icon">👁️</button>
                            {ts.status !== 'submitted' && (
                              <button
                                className="btn-small btn-icon"
                                onClick={() => handleDelete(ts.id)}
                              >
                                🗑️
                              </button>
                            )}
                          </div>
                        </td>
                      </tr>
                      {ts.status === 'error' && ts.errorMessage && (
                        <tr key={`${ts.id}-error`} className="error-row">
                          <td colSpan="4" style={{ 
                            backgroundColor: '#fee', 
                            padding: '12px', 
                            borderLeft: '4px solid #dc3545',
                            color: '#721c24'
                          }}>
                            <strong>⚠️ Error:</strong> {ts.errorMessage}
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  ))
                )}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </Layout>
  );
};

export default Upload;

